/**
 * UnifiedScanPage — 内部运营后台统一扫码入口（Issue #186）。
 *
 * 体验红线：
 *   - 扫码枪以键盘输入 + Enter 提交工作；本页面默认聚焦输入框并监听 form 提交。
 *   - 不允许做纯前端假状态；任何提交都通过 typed api-client 走后端
 *     `/api/v1/scan/resolve`，后端写 scan_events 审计。
 *   - 失败结果（过期 / 作废 / 跨模块 / 签名错误 / 重复扫码 / 未注册码 / 越权）
 *     必须如实展示给操作人。
 *
 * 安全红线：
 *   - 二维码 payload 仅承载短码 / 签名 / nonce / 过期时间 / business_type；
 *     页面回显 resourceId / businessNo 也仅是业务键，不展示客户姓名、手机号、
 *     身份证号、价格、财务或细胞结果。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useResolveScan,
  type ResolveScanRequest,
  type ResolveScanResponse,
} from '@/hooks/useScan';

const BUSINESS_TYPE_OPTIONS = [
  { value: '', label: '不限（自动按 QR 自带类型识别）' },
  { value: 'sample', label: '样本' },
  { value: 'cell_batch', label: '细胞批次' },
  { value: 'qc_order', label: '质检单' },
  { value: 'release_order', label: '放行单' },
  { value: 'infusion_order', label: '回输单' },
  { value: 'transfer_order', label: '中转单' },
  { value: 'visit_order', label: '就诊单' },
  { value: 'material_batch', label: '物料批次' },
  { value: 'equipment', label: '设备' },
  { value: 'cold_chain', label: '冷链' },
  { value: 'inventory_count', label: '盘点' },
  { value: 'gift_batch', label: '礼品' },
  { value: 'contract', label: '合同' },
  { value: 'quote', label: '报价' },
  { value: 'payment_record', label: '收款单' },
  { value: 'invoice', label: '发票' },
  { value: 'complaint_ticket', label: '投诉工单' },
  { value: 'followup_survey', label: '随访问卷' },
  { value: 'ai_report', label: 'AI 报告' },
] as const;

const SOURCE_OPTIONS = [
  { value: 'scanner_gun', label: '扫码枪' },
  { value: 'mobile_camera', label: '摄像头' },
  { value: 'manual_input', label: '手工输入' },
] as const;

const RESULT_LABEL: Record<ResolveScanResponse['result'], string> = {
  resolved: '解析成功',
  invalid_format: '二维码格式错误',
  not_found: '二维码未在当前组织登记',
  signature_mismatch: '签名不通过，可能被篡改',
  expired: '二维码已过期',
  revoked: '二维码已作废',
  consumed: '二维码已被使用',
  business_type_mismatch: '业务类型不匹配（疑似跨模块扫码）',
  duplicate: '重复扫码',
  cross_module: '跨模块扫码',
  tenant_mismatch: '跨租户扫码尝试',
  rbac_denied: '权限不足',
  error: '系统错误',
};

interface HistoryEntry {
  readonly id: string;
  readonly scannedAt: string;
  readonly rawCode: string;
  readonly result: ResolveScanResponse['result'];
  readonly businessType: ResolveScanResponse['businessType'];
  readonly resourceId: string | null;
  readonly businessNo: string | null;
  readonly failureReason: string | null;
}

export default function UnifiedScanPage(): ReactElement {
  const [rawCode, setRawCode] = useState('');
  const [source, setSource] = useState<ResolveScanRequest['source']>('scanner_gun');
  const [expectedBusinessType, setExpectedBusinessType] = useState<string>('');
  const [businessContext, setBusinessContext] = useState('');
  const [markConsumed, setMarkConsumed] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const { loading, error, data, resolve, reset } = useResolveScan();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 扫码枪以 Enter 提交：页面挂载即聚焦输入框，便于直接“扫一下、回车提交”。
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      const trimmed = rawCode.trim();
      if (!trimmed) {
        return;
      }
      // 去除扫码枪可能带入的前后空白与不可见字符（ASCII 控制字符 0x00–0x1F）。
      const cleaned = Array.from(trimmed)
        .filter((ch) => ch.charCodeAt(0) >= 0x20)
        .join('');
      const payload: ResolveScanRequest = {
        rawCode: cleaned,
        source,
        channel: 'admin_web',
        ...(expectedBusinessType
          ? { expectedBusinessType: expectedBusinessType as ResolveScanRequest['expectedBusinessType'] }
          : {}),
        ...(businessContext.trim() ? { businessContext: businessContext.trim() } : {}),
        ...(markConsumed ? { markConsumed: true } : {}),
      };
      const resp = await resolve(payload);
      // 总是把结果落到历史区（无论失败还是成功，扫码枪连续扫码时方便回看）。
      const entry: HistoryEntry = {
        id: resp?.scanEventId ?? `local_${Date.now()}`,
        scannedAt: new Date().toISOString(),
        rawCode: cleaned,
        result: resp ? resp.result : 'error',
        businessType: resp ? resp.businessType : 'unknown',
        resourceId: resp?.resourceId ?? null,
        businessNo: resp?.businessNo ?? null,
        failureReason: resp?.failureReason ?? error?.message ?? null,
      };
      setHistory((prev) => [entry, ...prev].slice(0, 20));
      setRawCode('');
      // 重新聚焦，使下一次扫码即时进入。
      inputRef.current?.focus();
    },
    [businessContext, error, expectedBusinessType, markConsumed, rawCode, resolve, source],
  );

  const currentResult = useMemo(() => {
    if (!data) return null;
    return RESULT_LABEL[data.result];
  }, [data]);

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="unified-scan-page">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">统一扫码</h1>
        <p className="text-sm text-muted-foreground">
          支持扫码枪与摄像头扫码。二维码不携带客户姓名、身份证、手机号、价格、
          细胞结果等敏感字段，后端解析后写入 scan_events 审计。
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">扫码输入</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="scan-form">
            <div className="flex flex-col gap-2">
              <Label htmlFor="scan-input">二维码 / 扫码枪输入</Label>
              <Input
                id="scan-input"
                ref={inputRef}
                value={rawCode}
                onChange={(e) => setRawCode(e.target.value)}
                placeholder="将扫码枪焦点对准本输入框扫描，或粘贴二维码字符串后按回车"
                autoFocus
                autoComplete="off"
                spellCheck={false}
                data-testid="scan-input"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="scan-source">扫码设备</Label>
                <select
                  id="scan-source"
                  className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  value={source}
                  onChange={(e) => setSource(e.target.value as ResolveScanRequest['source'])}
                  data-testid="scan-source-select"
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="scan-expected-type">期望业务类型</Label>
                <select
                  id="scan-expected-type"
                  className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  value={expectedBusinessType}
                  onChange={(e) => setExpectedBusinessType(e.target.value)}
                  data-testid="scan-expected-business-type"
                >
                  {BUSINESS_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value || 'any'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="scan-context">业务场景（仅审计）</Label>
                <Input
                  id="scan-context"
                  value={businessContext}
                  onChange={(e) => setBusinessContext(e.target.value)}
                  maxLength={80}
                  placeholder="例如 lab/sample-records/create"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="scan-mark-consumed"
                type="checkbox"
                checked={markConsumed}
                onChange={(e) => setMarkConsumed(e.target.checked)}
                data-testid="scan-mark-consumed"
              />
              <Label htmlFor="scan-mark-consumed" className="text-sm text-muted-foreground">
                一次性 Token：解析成功后将 QR 标记为 consumed
              </Label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading || !rawCode.trim()} data-testid="scan-submit">
                {loading ? '解析中…' : '解析（Enter）'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setRawCode('');
                  reset();
                  inputRef.current?.focus();
                }}
              >
                清空
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card data-testid="scan-result-card">
        <CardHeader>
          <CardTitle className="text-base">最近一次扫码结果</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-destructive" data-testid="scan-error">
              {error.message}
            </div>
          ) : data ? (
            <div className="flex flex-col gap-2 text-sm">
              <div data-testid="scan-result-label">
                <span className="text-muted-foreground">结果：</span>
                <span
                  className={
                    data.result === 'resolved'
                      ? 'font-semibold text-primary'
                      : 'font-semibold text-destructive'
                  }
                >
                  {currentResult}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">业务类型：</span>
                {data.businessType}
              </div>
              <div>
                <span className="text-muted-foreground">业务对象 id：</span>
                {data.resourceId ?? '—'}
              </div>
              <div>
                <span className="text-muted-foreground">业务编号：</span>
                {data.businessNo ?? '—'}
              </div>
              <div>
                <span className="text-muted-foreground">过期时间：</span>
                {data.expiresAt ?? '—'}
              </div>
              {data.failureReason ? (
                <div className="text-destructive">
                  <span className="text-muted-foreground">失败原因：</span>
                  {data.failureReason}
                </div>
              ) : null}
              <div className="text-xs text-muted-foreground">
                scan_event id: {data.scanEventId}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">尚未扫描。</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">最近 20 条扫码记录</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-sm text-muted-foreground">暂无记录。</div>
          ) : (
            <ul className="flex flex-col gap-2 text-sm" data-testid="scan-history">
              {history.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-col gap-1 rounded-md border border-border p-2"
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span
                      className={
                        entry.result === 'resolved'
                          ? 'font-medium text-primary'
                          : 'font-medium text-destructive'
                      }
                    >
                      {RESULT_LABEL[entry.result]}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.scannedAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    类型：{entry.businessType} · 业务编号：{entry.businessNo ?? '—'} ·
                    资源 id：{entry.resourceId ?? '—'}
                  </div>
                  {entry.failureReason ? (
                    <div className="text-xs text-destructive">原因：{entry.failureReason}</div>
                  ) : null}
                  <code className="truncate text-[11px] text-muted-foreground">
                    {entry.rawCode}
                  </code>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
